import React, { useState } from 'react';
import { X, Trash2, GripVertical, Plus, Check } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RyanSpaceGroupData } from '../types/ryan.types';

interface RyanManageSpaceGroupsModalProps {
  groups: RyanSpaceGroupData[];
  onClose: () => void;
  onChange: (groups: RyanSpaceGroupData[]) => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const SortableGroupRow: React.FC<{
  group: RyanSpaceGroupData;
  isEditing: boolean;
  draftName: string;
  draftTag: string;
  onStartEdit: () => void;
  onChangeDraftName: (v: string) => void;
  onChangeDraftTag: (v: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
}> = ({ group, isEditing, draftName, draftTag, onStartEdit, onChangeDraftName, onChangeDraftTag, onSaveEdit, onCancelEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group._id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
        background: '#f8faf8', borderRadius: 10, border: '1px solid rgba(13,59,14,0.08)',
      }}>
        <div {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--text-secondary)', display: 'flex', flexShrink: 0 }}>
          <GripVertical size={15} />
        </div>

        {isEditing ? (
          <>
            <input
              className="ryan-modal-input"
              style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
              value={draftName}
              onChange={e => onChangeDraftName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSaveEdit()}
              autoFocus
              placeholder="Nome do grupo"
            />
            <input
              className="ryan-modal-input"
              style={{ width: 110, padding: '6px 10px', fontSize: 13 }}
              value={draftTag}
              onChange={e => onChangeDraftTag(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSaveEdit()}
              placeholder="Tag (opcional)"
              title="Só aparece pra contas com essa tag de instituição — vazio = grupo público"
            />
          </>
        ) : (
          <span
            style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: '#111', fontFamily: 'Inter, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
            onClick={onStartEdit}
          >
            {group.name}
            {group.requiredInstitutionTag && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: '#0D3B0E', background: 'rgba(13,59,14,0.08)',
                borderRadius: 6, padding: '2px 7px',
              }}>
                {group.requiredInstitutionTag}
              </span>
            )}
          </span>
        )}

        {isEditing ? (
          <>
            <button className="ryan-group-kebab-btn" onClick={onSaveEdit} title="Salvar">
              <Check size={15} />
            </button>
            <button className="ryan-group-kebab-btn" onClick={onCancelEdit} title="Cancelar">
              <X size={15} />
            </button>
          </>
        ) : (
          <button className="ryan-group-kebab-btn" onClick={onDelete} title="Excluir grupo" style={{ color: '#dc2626' }}>
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
};

const RyanManageSpaceGroupsModal: React.FC<RyanManageSpaceGroupsModalProps> = ({
  groups, onClose, onChange, onToast,
}) => {
  const [items, setItems] = useState<RyanSpaceGroupData[]>(
    () => [...groups].sort((a, b) => a.order - b.order)
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftTag, setDraftTag] = useState('');
  const [newName, setNewName] = useState('');
  const [newTag, setNewTag] = useState('');
  const [creating, setCreating] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const token = () => localStorage.getItem('accessToken') || '';

  const applyChange = (next: RyanSpaceGroupData[]) => {
    setItems(next);
    onChange(next);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex(g => g._id === active.id);
    const newIdx = items.findIndex(g => g._id === over.id);
    const reordered = arrayMove(items, oldIdx, newIdx);
    applyChange(reordered);

    try {
      const res = await fetch('/api/ryan/admin/space-groups/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ groups: reordered.map((g, i) => ({ _id: g._id, order: i })) }),
      });
      if (res.ok) applyChange(await res.json());
    } catch {
      onToast('Erro ao reordenar grupos', 'error');
    }
  };

  const startEdit = (group: RyanSpaceGroupData) => {
    setEditingId(group._id);
    setDraftName(group.name);
    setDraftTag(group.requiredInstitutionTag || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftName('');
    setDraftTag('');
  };

  const saveEdit = async (groupId: string) => {
    if (!draftName.trim()) { onToast('Nome obrigatório', 'error'); return; }
    try {
      const res = await fetch(`/api/ryan/admin/space-groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ name: draftName.trim(), requiredInstitutionTag: draftTag.trim() || null }),
      });
      if (!res.ok) { onToast('Erro ao renomear grupo', 'error'); return; }
      const updated = await res.json();
      applyChange(items.map(g => g._id === groupId ? updated : g));
      setEditingId(null);
    } catch {
      onToast('Erro de conexão', 'error');
    }
  };

  const deleteGroup = async (group: RyanSpaceGroupData) => {
    if (!window.confirm(`Excluir grupo "${group.name}"? Os decks dele ficam em "Sem Grupo", nenhum é apagado.`)) return;
    try {
      const res = await fetch(`/api/ryan/admin/space-groups/${group._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const e = await res.json();
        onToast(e.message || 'Erro ao excluir grupo', 'error');
        return;
      }
      applyChange(items.filter(g => g._id !== group._id));
      onToast('Grupo excluído!', 'success');
    } catch {
      onToast('Erro de conexão', 'error');
    }
  };

  const createGroup = async () => {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const res = await fetch('/api/ryan/admin/space-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ name: newName.trim(), requiredInstitutionTag: newTag.trim() || null }),
      });
      if (!res.ok) {
        const e = await res.json();
        onToast(e.message || 'Erro ao criar grupo', 'error');
        return;
      }
      const created = await res.json();
      applyChange([...items, created]);
      setNewName('');
      setNewTag('');
    } catch {
      onToast('Erro de conexão', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="ryan-modal-overlay" onClick={onClose}>
      <div className="ryan-modal ryan-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="ryan-modal-header">
          <h3 className="ryan-modal-title">Gerenciar Grupos</h3>
          <button className="ryan-modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ryan-modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 14 }}>
            Arraste para reordenar as seções da home. Clique no nome pra renomear ou definir uma tag de
            instituição (opcional) — com uma tag, o grupo só aparece pra contas que a tenham.
          </p>

          {items.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: '24px 0', fontFamily: 'Inter, sans-serif' }}>
              Nenhum grupo ainda.
            </p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map(g => g._id)} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map(group => (
                    <SortableGroupRow
                      key={group._id}
                      group={group}
                      isEditing={editingId === group._id}
                      draftName={draftName}
                      draftTag={draftTag}
                      onStartEdit={() => startEdit(group)}
                      onChangeDraftName={setDraftName}
                      onChangeDraftTag={setDraftTag}
                      onSaveEdit={() => saveEdit(group._id)}
                      onCancelEdit={cancelEdit}
                      onDelete={() => deleteGroup(group)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <input
              className="ryan-modal-input"
              style={{ flex: 1 }}
              placeholder="Novo grupo…"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createGroup()}
            />
            <input
              className="ryan-modal-input"
              style={{ width: 110 }}
              placeholder="Tag (opcional)"
              value={newTag}
              onChange={e => setNewTag(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createGroup()}
              title="Só aparece pra contas com essa tag de instituição — vazio = grupo público"
            />
            <button className="ryan-modal-save-btn" onClick={createGroup} disabled={creating || !newName.trim()}>
              <Plus size={14} /> Criar
            </button>
          </div>
        </div>

        <div className="ryan-modal-footer">
          <button className="ryan-modal-cancel-btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default RyanManageSpaceGroupsModal;
